import { useState } from "react";
import { toast } from "sonner";
import { Loader2, UserX, ChevronDown, Plus, Bike, Car } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { api } from "../lib/api";

const isActive = (st) => !st || st.includes("نشط") || st.toLowerCase() === "active";
const isSaudi = (n = "") => /سعود|saudi/i.test(n);
const empKind = (t = "") => (/سيار|car/i.test(t) ? "car" : /دراج|motor/i.test(t) ? "moto" : "");

// Moto / car switch for a card (manual override of the auto-detected kind).
export const CardKindToggle = ({ card, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const set = async (kind) => {
    if (kind === card.vehicle_kind) return;
    setBusy(true);
    try { await api.setOperatingCardKind(card.number, kind); toast.success(`الكرت ${card.number}: ${kind === "car" ? "سيارة (مندوب واحد)" : "دراجة نارية (3 مناديب)"}`); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التغيير"); }
    finally { setBusy(false); }
  };
  const btn = (k, Icon, label) => (
    <button type="button" data-testid={`opcard-kind-${k}-${card.number}`} disabled={busy} onClick={() => set(k)} title={label}
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${card.vehicle_kind === k ? (k === "car" ? "bg-violet-600 text-white" : "bg-sky-600 text-white") : "bg-white text-slate-500 hover:bg-slate-100"}`}>
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
  return (
    <span className="inline-flex overflow-hidden rounded-full border border-slate-200" data-testid={`opcard-kind-${card.number}`}>
      {btn("moto", Bike, "دراجة")}{btn("car", Car, "سيارة")}
      {busy && <Loader2 className="h-3 w-3 animate-spin mx-1 self-center" />}
    </span>
  );
};

// Active riders that have no operating card, each with a quick "add to card" picker.
export const NoCardRiders = ({ employees, riderCards, cards, onChanged }) => {
  const [open, setOpen] = useState(false);
  const list = employees.filter((e) => isActive(e.status) && !isSaudi(e.nationality) && !(riderCards[e.id] || []).length);
  if (!employees.length) return null;
  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50/60" data-testid="opcards-no-card">
      <button type="button" data-testid="opcards-no-card-toggle" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-orange-800">
        <span className="flex items-center gap-2"><UserX className="h-4 w-4" /> مناديب نشطون (مقيمون) بلا كرت تشغيل: <b data-testid="opcards-no-card-count">{list.length}</b></span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="max-h-64 overflow-y-auto border-t border-orange-200 p-2 space-y-1">
          {list.length === 0 ? <p className="py-3 text-center text-xs text-emerald-700">كل المناديب النشطين لديهم كرت</p> : list.map((e) => (
            <div key={e.id} data-testid={`opcards-no-card-row-${e.id}`} className="flex items-center justify-between gap-2 rounded-lg bg-white px-2 py-1.5 text-xs">
              <span className="truncate">{e.name}{e.branch ? <span className="text-slate-400"> — {e.branch}</span> : null}
                {empKind(e.vehicle_type) && <span className="mr-1 text-slate-400">· {empKind(e.vehicle_type) === "car" ? "سيارة" : "دراجة"}</span>}</span>
              <AssignToCard emp={e} cards={cards} onDone={onChanged} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AssignToCard = ({ emp, cards, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const kind = empKind(emp.vehicle_type);
  const targets = cards.filter((c) => c.riders_count < (c.max_riders || 3) && (!kind || c.vehicle_kind === kind))
    .sort((a, b) => b.riders_count - a.riders_count);
  const add = async (num) => {
    setBusy(num);
    try { await api.addOperatingCardRider(num, emp.id); toast.success(`تمت إضافة ${emp.name} على الكرت ${num}`); setOpen(false); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت الإضافة"); }
    finally { setBusy(""); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`opcards-assign-${emp.id}`} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-orange-300 px-2 py-0.5 text-[11px] font-semibold text-orange-700 transition-colors hover:bg-orange-100">
          <Plus className="h-3 w-3" /> إضافة لكرت
        </button>
      </PopoverTrigger>
      <PopoverContent dir="rtl" align="start" className="w-64 p-2">
        <p className="px-1 pb-2 text-xs font-semibold text-slate-600">كروت فيها مكان{kind ? ` (${kind === "car" ? "سيارات" : "دراجات"})` : ""}:</p>
        {targets.length === 0 ? <p className="py-2 text-center text-xs text-slate-400">لا توجد كروت فيها مكان</p> : (
          <div className="max-h-56 overflow-y-auto space-y-1">
            {targets.map((c) => (
              <button key={c.number} type="button" data-testid={`opcards-assign-to-${c.number}`} disabled={!!busy} onClick={() => add(c.number)}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2 py-1.5 text-xs transition-colors hover:bg-emerald-50">
                <span className="font-mono-nums font-semibold" dir="ltr">{c.number}</span>
                <span className="text-slate-400 truncate">{c.plate_number}</span>
                {busy === c.number ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="text-emerald-700">{(c.max_riders || 3) - c.riders_count} مكان</span>}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
